import React from 'react';
import { createPortal } from 'react-dom';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import { produce } from 'immer';
import { uniqueId } from 'lodash-es';
import { createRoot } from 'react-dom/client';
import Modal, { IModal } from './Modal';

type Store = {
  jsxMap: Record<string, JSX.Element>;
};

const store = createStore<Store>(() => ({
  jsxMap: {},
}));

type ModalProps = Omit<IModal, 'onClose'> & { onClose?: () => void };

export type ResolveReject = {
  resolve: (val: unknown) => void;
  reject: (reason?: $TSFixMe) => void;
};

type BaseModalProps = Omit<ModalProps, 'visible' | 'footer' | 'children'>;

type ChildModalProps = BaseModalProps & {
  children: (args: ResolveReject) => JSX.Element;
  _internal: ResolveReject;
  modalPropsWithActions?: (args: ResolveReject) => BaseModalProps;
};

const CANCEL = `CANCEL`;

const ChildModal = ({
  children,
  _internal,
  modalPropsWithActions,
  ...rest
}: ChildModalProps) => {
  return (
    <Modal
      {...rest}
      onClose={() => _internal.reject(CANCEL)}
      {...modalPropsWithActions?.(_internal)}
      visible
      footer={null}
    >
      {children(_internal)}
    </Modal>
  );
};

export type TAsyncModalArgs = Omit<ChildModalProps, '_internal'>;

const asyncModal = (props: TAsyncModalArgs) => {
  const { setState } = store;
  const myId = uniqueId();
  const div = document.createElement('div');
  document.body.appendChild(div);
  const root = createRoot(div);

  function destory() {
    setState(
      produce((draft: Store) => {
        draft.jsxMap[myId] = <></>;
      })
    );
    root.unmount();
    if (div.parentNode) {
      div.parentNode.removeChild(div);
    }
  }

  return new Promise((res, rej) => {
    setState(
      produce((draft: Store) => {
        draft.jsxMap[myId] = createPortal(
          <ChildModal
            {...props}
            _internal={{
              resolve: (val) => {
                destory();
                res(val);
              },
              reject: (err) => {
                destory();
                rej(err);
              },
            }}
          />,
          div
        );
      })
    );
  });
};

asyncModal.CANCEL = CANCEL;

export { asyncModal };

export const AsyncModalPortal = () => {
  const jsxMap = useStore(store, (state) => state.jsxMap);

  return <>{Object.values(jsxMap)}</>;
};
